import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import helperStyle from '~/components/styles/helpers.module.scss';
import { SelectedSort, SortingMethod, SortingMethods } from '~/core/features/sorting/types/sorting.type';
import styles from './sort.module.scss';

interface ISort {
    selectedSort: SelectedSort;
    sortingMethods: SortingMethods;
    sortResults: (sort: SelectedSort) => void;
}

const Sort: React.FC<ISort> = ({ selectedSort, sortingMethods, sortResults }) => {
    const intl = useIntl();
    const defaultSort = intl.formatMessage({
        id: 'default_sorting',
        defaultMessage: 'Relevance',
    });
    const [isOpen, setIsOpen] = useState(false);
    const [sortName, setSortName] = useState(defaultSort);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sort = sortingMethods.find(
            (sortingMethod) => sortingMethod.key === selectedSort?.field && sortingMethod.type === selectedSort?.order
        );
        if (sort) {
            setSortName(sort.name);
        } else {
            setSortName(defaultSort);
        }
    }, [selectedSort]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
          }
        };
    
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, []);

    const handleSortClick = (key?: string, sortType?: 'asc' | 'desc'): void => {
        setIsOpen(false);
        if (!key || !sortType) {
            sortResults(null);
            return;
        }
        sortResults({ field: key, order: sortType });
    };

    return (
        <div className={styles.viewOptions}>
            <div className={classNames(styles.sortingMethods, helperStyle.textSmall)} data-testid="sorting">
                <label className={styles.sortingMethodsLabel} htmlFor="dropdownMenuButton">
                    <FormattedMessage id="sort_by" defaultMessage="Řazeno dle" />
                </label>
                <div className={styles.dropdown} ref={dropdownRef}>
                    <button
                        className={classNames(styles.dropdownToggle, !isOpen ? styles.show : '')}
                        type="button"
                        id="dropdownMenuButton"
                        aria-expanded={isOpen ? true : false}
                        aria-controls="dropdownMenuList"
                        onClick={() => setIsOpen(!isOpen)}
                        data-testid="sorting-dropdown-button"
                    >
                        {sortName}
                        <svg
                            style={{
                                width: '20px',
                                height: '20px',
                            }}
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path fill="currentColor" d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
                        </svg>
                    </button>
                    {isOpen ? (
                        <ul
                            className={styles.dropdownMenu}
                            aria-labelledby="dropdownMenuButton"
                            id="dropdownMenuList"
                            data-testid="sorting-options-list"
                        >
                            {sortName !== defaultSort ? (
                                <li key={0}>
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleSortClick(defaultSort)}
                                    >
                                        {defaultSort}
                                    </button>
                                </li>
                            ) : null}
                            {sortingMethods
                                .sort((a, b) => (a.order > b.order ? 1 : -1))
                                .map((sort: SortingMethod, i) => {
                                    return (
                                        sort.name !== sortName && (
                                            <li key={i + 1}>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => handleSortClick(sort.key, sort.type)}
                                                >
                                                    {sort.name}
                                                </button>
                                            </li>
                                        )
                                    );
                                })}
                        </ul>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default Sort;
